<script setup lang="ts">
import { computed } from 'vue'
import type { PageDefinition, SelectOption } from '@/types/page'
import { usePrototypeStore } from '@/stores/prototype'
import ProSelect from './ProSelect.vue'
import ProDateRange from './ProDateRange.vue'
import AppIcon from '@/components/AppIcon.vue'

const props = defineProps<{
  definition: PageDefinition
  modelValue: Record<string, unknown>
  keyword: string
  activeCount: number
}>()

const emit = defineEmits<{
  'update:modelValue': [value: Record<string, unknown>]
  'update:keyword': [value: string]
  reset: []
  search: []
}>()

const store = usePrototypeStore()
const visibleFilters = computed(() => props.definition.filters.filter(item => item.type !== 'keyword'))

function options(source: string | undefined, inline: SelectOption[] | undefined): SelectOption[] {
  if (source) return store.options(props.definition, source as never)
  return inline ?? []
}

function update(key: string, value: unknown): void {
  emit('update:modelValue', { ...props.modelValue, [key]: value })
}

function valueFor(key: string): unknown {
  return props.modelValue[key] ?? (props.definition.filters.find(item => item.key === key)?.type === 'daterange' ? [] : '')
}

function filterClass(width?: number): string {
  return width && width >= 200 ? 'filter-control filter-control--lg' : width && width <= 108 ? 'filter-control filter-control--sm' : 'filter-control'
}
</script>

<template>
  <div class="pro-filterbar" role="search" aria-label="列表筛选">
    <div class="search-field">
      <AppIcon name="search" :size="16" class="search-field__icon" />
      <el-input
        :model-value="keyword"
        :placeholder="definition.filters.find(item => item.type === 'keyword')?.placeholder || '搜索当前列表'"
        clearable
        aria-label="搜索关键词"
        @update:model-value="emit('update:keyword', $event)"
        @keyup.enter="emit('search')"
      />
    </div>

    <div
      v-for="filter in visibleFilters"
      :key="filter.key"
      class="pro-filterbar__field"
      :class="filterClass(filter.width)"
    >
      <ProDateRange
        v-if="filter.type === 'daterange'"
        :model-value="(valueFor(filter.key) as string[])"
        :placeholder="[filter.placeholder || '开始日期', '结束日期']"
        :aria-label="filter.label"
        @update:model-value="update(filter.key, $event ?? [])"
      />
      <ProSelect
        v-else
        :model-value="valueFor(filter.key) as string"
        :options="options(filter.optionSource, filter.options)"
        :placeholder="filter.placeholder || `全部${filter.label}`"
        :multiple="filter.type === 'multiselect'"
        :clearable="true"
        :aria-label="filter.label"
        @update:model-value="update(filter.key, $event)"
      />
    </div>

    <button type="button" class="btn btn--sm pro-filterbar__reset" :disabled="!activeCount" @click="emit('reset')">
      <AppIcon name="refresh" :size="13" />重置
    </button>
    <span v-if="activeCount" class="filter-active-count">{{ activeCount }} 项筛选</span>
  </div>
</template>

<style scoped>
.pro-filterbar {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  padding: 12px 16px; margin-bottom: 16px;
  background: #fff; border: 1px solid var(--c-border); border-radius: var(--radius-card); box-shadow: var(--shadow-card);
}
.pro-filterbar__field { min-width: 0; }
.search-field { flex: 1 1 260px; max-width: 360px; min-width: 220px; }
.pro-filterbar__reset { flex: 0 0 auto; color: var(--c-muted); }
.filter-active-count { font-size: 10px; color: var(--c-brand-strong); background: var(--c-brand-soft); border-radius: 999px; padding: 2px 7px; white-space: nowrap; }
@media (max-width: 767px) {
  .pro-filterbar { padding: 10px 12px; gap: 7px; }
  .search-field { flex-basis: 100%; max-width: none; min-width: 0; }
  .pro-filterbar__field { flex: 1 1 46%; }
  .pro-filterbar__reset { margin-left: auto; }
}
</style>
